import { Box, Grid } from '@mui/material';

import {
  InfoReviewFieldLabel,
  InfoReviewFieldValue,
} from '@/components/Form/Form.styled';
import { Divider } from '@/components/Layout/Divider';

import { MARITAL_STATUS_TEXT, TitleText } from '@/constants/user';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLivenessrecordDisabled } from '@/redux/configs/baseSelectors';
import { selectConfigsState } from '@/redux/configs/selectors';
import { selectUserData } from '@/redux/user-data/selectors';

import {
  Document,
  DocumentSection,
  DocumentStatus,
  RequiredCondition,
} from '@/types/document';
import { Language } from '@/types/i18n';

import {
  findActiveDoc,
  findActiveDocs,
  getHigherStatus,
} from '@/utils/document';
import { getDocumentTypeName } from '@/utils/signingDocument';
import {
  formatDateValue,
  getDisplayedPhone,
  getFullName,
  maskName,
} from '@/utils/string';
import {
  checkUserEkycStatus,
  getDisplayCountryName,
  isEkycStatusComplete,
  isRemediationCustomer,
} from '@/utils/user';

import { ReviewCard } from './ReviewCard';
import { SectionProps } from './types';

export function PersonalDetailsSection({
  currentIndex,
  totalSections,
}: Readonly<SectionProps>) {
  const { t, country, nationality } = useTranslate();
  const { user, requiredDocumentSections } = useAppSelector(selectUserData);
  const { shouldShowMotherMaidenName, dateFormat } =
    useAppSelector(selectConfigsState);
  const isLivenessRecordDisabled = useAppSelector(selectLivenessrecordDisabled);
  const language = (user?.language as Language) || Language.English;

  const primaryId = findActiveDoc(
    user?.documents ?? [],
    DocumentSection.Identity,
  );
  const additionalIdDocuments = findActiveDocs(user?.documents ?? [], [
    DocumentSection.SecondID,
    DocumentSection.SecondCitizenshipID,
  ]);

  const isRemediation = isRemediationCustomer(user);

  const isIdentityCheckCompleted = isEkycStatusComplete(
    checkUserEkycStatus(user),
    isLivenessRecordDisabled,
  );

  const additionalIdStatuses = (requiredDocumentSections ?? [])
    .filter(
      section =>
        String(section.type) === DocumentSection.SecondID ||
        String(section.type) === DocumentSection.SecondCitizenshipID,
    )
    .map(section => section.status as DocumentStatus);

  const additionalIdStatus = getHigherStatus(
    additionalIdStatuses,
    RequiredCondition.And,
  );

  const isAdditionalIdCompleted =
    additionalIdStatuses.length > 0 &&
    (additionalIdStatus === DocumentStatus.InReview ||
      additionalIdStatus === DocumentStatus.Approved);

  const showIdentityCheck = !isRemediation || isIdentityCheckCompleted;
  const showAdditionalId = !isRemediation || isAdditionalIdCompleted;

  const identityDocuments = [
    ...(primaryId && showIdentityCheck ? [primaryId] : []),
    ...(showAdditionalId ? additionalIdDocuments : []),
  ];

  const fieldPrefix = 'moreInfo.reviewMyInformation.personalDetails.field';
  const genderKey = user?.gender === 'M' ? 'male' : 'female';
  const showBranch = !isRemediation;

  return (
    <ReviewCard
      title={t('moreInfo.reviewMyInformation.personalDetails.title')}
      currentIndex={currentIndex}
      totalSections={totalSections}
    >
      {/* General Details */}
      <InfoReviewFieldLabel>
        {t(`${fieldPrefix}.fullName`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>{getFullName(user)}</InfoReviewFieldValue>

      {user?.title && (
        <>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.title`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {TitleText[user.title] || user.title}
          </InfoReviewFieldValue>
        </>
      )}

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.gender`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {user?.gender ? t(`gender.${genderKey}`) : ''}
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.maritalStatus`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {user?.maritalStatus
              ? t(MARITAL_STATUS_TEXT[user.maritalStatus])
              : ''}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.dateOfBirth`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {user?.birthday ? formatDateValue(user.birthday, dateFormat) : ''}
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.countryOfBirth`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {country[user?.countryOfBirth as string] ||
              getDisplayCountryName(user?.countryOfBirth, language)}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.phoneNumber`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {getDisplayedPhone(user?.phoneNumber)}
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.phoneNumberAlt`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {getDisplayedPhone(user?.phoneNumberAlt)}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>

      <InfoReviewFieldLabel>{t(`${fieldPrefix}.email`)}</InfoReviewFieldLabel>
      <InfoReviewFieldValue sx={!showBranch ? { mb: 2 } : undefined}>
        {user?.email}
      </InfoReviewFieldValue>

      <Grid container spacing={2}>
        {showBranch && (
          <Grid item xs={6}>
            <InfoReviewFieldLabel>
              {t(`${fieldPrefix}.branch`)}
            </InfoReviewFieldLabel>
            <InfoReviewFieldValue>{user?.branchName}</InfoReviewFieldValue>
          </Grid>
        )}
        {shouldShowMotherMaidenName && user?.motherMaidenName && (
          <Grid item xs={6}>
            <InfoReviewFieldLabel>
              {t(`${fieldPrefix}.motherMaidenName`)}
            </InfoReviewFieldLabel>
            <InfoReviewFieldValue>
              {maskName(user.motherMaidenName)}
            </InfoReviewFieldValue>
          </Grid>
        )}
      </Grid>

      <Divider width={'100%'} sx={{ mt: 1.5 }} />

      {/* Identity Details */}
      {identityDocuments.length > 0 && (
        <>
          {identityDocuments.map((doc: Document) => (
            <IdentityDocumentItem
              key={doc.id}
              doc={doc}
              isAdditionalId={doc.type !== DocumentSection.Identity}
              dateFormat={dateFormat}
              language={language}
            />
          ))}

          <Divider width={'100%'} sx={{ mt: 1.5 }} />
        </>
      )}

      {/* Citizenship */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.citizenship`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {nationality[user?.citizenship as string] || user?.citizenship}
          </InfoReviewFieldValue>
        </Grid>
        {user?.citizenship2Has && user?.citizenship2 && (
          <Grid item xs={6}>
            <InfoReviewFieldLabel>
              {t(`${fieldPrefix}.citizenship2`)}
            </InfoReviewFieldLabel>
            <InfoReviewFieldValue>
              {nationality[user.citizenship2] || user.citizenship2}
            </InfoReviewFieldValue>
          </Grid>
        )}
      </Grid>

      <Divider width={'100%'} sx={{ my: 2 }} />

      {/* US Status */}
      <InfoReviewFieldLabel>
        {t(`${fieldPrefix}.usIndicia`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {user?.usIndicia ? t('common.yes') : t('common.no')}
      </InfoReviewFieldValue>
    </ReviewCard>
  );
}

type IdentityDocumentItemProps = {
  doc: Document;
  isAdditionalId: boolean;
  dateFormat: string;
  language: Language;
};

function IdentityDocumentItem({
  doc,
  isAdditionalId,
  dateFormat,
  language,
}: Readonly<IdentityDocumentItemProps>) {
  const { t, country } = useTranslate();
  const fieldPrefix = 'moreInfo.reviewMyInformation.personalDetails.field';

  const documentLabel = isAdditionalId
    ? `${getDocumentTypeName(doc.docType, language)} (${t('documentSection.Second_ID.title')})`
    : getDocumentTypeName(doc.docType, language);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>{documentLabel}</InfoReviewFieldLabel>
          <InfoReviewFieldValue>{doc.docNumber}</InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.countryOfIssue`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {country[doc.issueCountry as string] ||
              getDisplayCountryName(doc.issueCountry, language)}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.dateOfIssue`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {doc.issueDate ? formatDateValue(doc.issueDate, dateFormat) : ''}
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.expiryDate`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {doc.expiryDate ? formatDateValue(doc.expiryDate, dateFormat) : ''}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>
    </Box>
  );
}
